/**
 * Item Kernel / Development OS (gh#2059) Faz 4 dilim-2 PR-1 — the `pm_agent_run`
 * WIRE VOCABULARY, and the `result_ref` SHAPE Faz 3 deferred.
 *
 * ── WHY THESE THREE TUPLES LEFT `schema-pg/pm/pmAgentRun.ts` ────────────────
 * They are WIRE DISCRIMINATORS, in the precise sense
 * [[feedback_cross_package_wire_discriminator_single_source]] names: the API writes
 * `action` / `host` / `status` into a `GET /api/dm/agent-runs` row, the web panel
 * reads them to decide what to render (and which badge colour to give a run), and
 * Faz 4's `dm.agent.launch` will take `action` and `host` as INPUT from a client.
 * Both halves are meaningless without the other, so the two are the same
 * declaration and must be one symbol — `@elmes/shared` is the only place both can
 * name it.
 *
 * The failure mode being closed is the one PR#1965 paid for and the one the panel's
 * own header already predicted in prose: while the tuples lived in the API's schema
 * tree, the web package could only either RETYPE them (a second authority that
 * starts refusing — or mis-colouring — a value the column can already store) or
 * fall back to bare `string` and lose the compiler entirely. It chose `string`, and
 * `dmPresentation.ts`'s badge map then carried four raw `pm_agent_run.status`
 * literals with NOTHING comparing them to the column's CHECK: rename `waiting_input`
 * in one PR and every suite stays GREEN while the badge silently falls through to
 * the neutral fallback. That is the same class as `PM_TENANT_SCOPE_ERROR_CODE`'s
 * near-miss, which `pmWireErrors.ts` resolved the same way, in this directory.
 *
 * ── ALL THREE, NOT JUST `status` ────────────────────────────────────────────
 * They are one block with one purpose: the three closed sets spec §2.4 gives
 * `pm_agent_run`, and §9's shared TS skeleton already declares `AgentRun` carrying
 * all three inline. Moving one and leaving two would split a documented family
 * across a package boundary and leave the next reader with a rule ("status is
 * shared, action and host are not") that has no reason behind it.
 *
 * ── SINGLE SOURCE, NOT A COPY: THE COLUMN STILL OWNS ENFORCEMENT ────────────
 * `pmAgentRun.ts` IMPORTS these tuples and feeds them to its `varchar(..., {enum})`
 * configs, which is what the baseline generator turns into
 * `chk_pm_agent_run_<column>`. So there is exactly ONE recording of each vocabulary
 * in the source tree, the catalog CHECK is generated FROM it, and
 * `pmAgentRunSchema.rls.pg.test.ts` compares `pg_get_constraintdef` back against
 * THESE constants — the loop that makes "the DB and the wire agree" a measurement
 * rather than a claim.
 *
 * ── AND WHAT DOES NOT MOVE ──────────────────────────────────────────────────
 * `PmAgentRunBriefRef` stays an OPEN `Record<string, unknown>` in the schema tree.
 * `brief_ref` is written by a dispatcher that does not exist yet from a builder
 * (`pm_type_def.ai_brief_template` + the item tree) that has not been designed, so
 * a shape declared here would be a shape GUESSED — the exact thing PR-8's header
 * refused to do for `result_ref` until a phase was ready to decide it. This PR is
 * that phase for `result_ref` and is not that phase for `brief_ref`.
 */
import { z } from 'zod';
/**
 * What the run was asked to DO (spec §2.4 / §6.3).
 *
 *   - `plan`   — produce a plan for the target (sprint / epic / item) without
 *                changing it; the output lands as a `dm.plan` item.
 *   - `start`  — execute: the implement flow, questions falling into pm_question.
 *   - `resume` — the OPTIONAL headless continuation (`claude -p --resume
 *                <sessionId>`) the pull-first decision (K-F0-5) keeps as a
 *                secondary path behind the live-session default.
 *   - `custom` — an operator-authored brief that is none of the three. Named in
 *                the spec's own vocabulary, so it is a closed set with an escape
 *                hatch rather than an open string.
 */
export const PM_AGENT_RUN_ACTION_VALUES = ['plan', 'start', 'resume', 'custom'];
/**
 * WHERE the agent process lives (spec §2.4, §9 `SessionRef`, §6.1).
 *
 *   - `server`  — this box: the BullMQ dispatcher's own `claude -p` child.
 *   - `cli`     — an operator's Claude CLI session reaching in over loopback MCP.
 *   - `desktop` — a remote machine over the `remoteMcp` OAuth surface.
 *
 * The same three words `SessionRef.host` uses in the spec's shared TS skeleton —
 * one vocabulary, so a run row and a `pm_question.session_ref` can be matched
 * without a translation table. Now that both live in THIS package, that sentence
 * is one import away from being enforceable rather than aspirational.
 */
export const PM_AGENT_RUN_HOST_VALUES = ['server', 'cli', 'desktop'];
/**
 * Run lifecycle (spec §2.4). A SMALL, CODE-OWNED closed set — unlike
 * `pm_item.status`, which is type_def-data-driven and therefore carries no enum
 * config at all. Nothing about a run is runtime-editable, so the catalog CHECK
 * generated from this tuple is the right enforcement level.
 *
 *   `queued` → `running` → (`waiting_input` ⇄ `running`) → `done` | `failed`
 *
 * `waiting_input` is the one that earns its own state rather than being derived:
 * the decision inbox (§7) and the panel's badge both key on it, and a run can sit
 * there indefinitely while its `pm_question` rows are open.
 */
export const PM_AGENT_RUN_STATUS_VALUES = [
    'queued',
    'running',
    'waiting_input',
    'done',
    'failed',
];
/**
 * WHAT PUT THE ROW THERE — DM W2 (D7), `0007_dm_w2_activity_ledger.sql`.
 *
 * `host` says where the process lives; this says which PRODUCER registered it, which is a
 * different question and the one the activity tree groups on. `host='cli'` alone cannot tell
 * an operator's own session apart from one of its sub-agents, and both from a scheduled job.
 *
 *   - `dispatcher`      — the BullMQ `claude -p` child (`host='server'`); the backfill's value
 *                         for every row that existed before this column.
 *   - `cli_interactive` — an operator's Claude CLI session, registered by the SessionStart
 *                         hook. The ROOT of a session tree (`parent_run_id IS NULL`,
 *                         `agent_id IS NULL`). All four `SessionStart.source` arms that create
 *                         a session (`startup|clear|fork|resume`) land here — the source word
 *                         is the hook's control flow, not a second origin.
 *   - `cli_subagent`    — a sub-agent of such a session (`agent_id` + `parent_run_id` set).
 *   - `cron`            — a scheduled producer.
 *   - `hook`            — a hook-initiated run that is nobody's CLI session.
 *
 * THE LAST TWO HAVE NO WRITER TODAY, and that is stated rather than left to be discovered: the
 * set is CLOSED by a catalog CHECK, and widening it later is an ALTER over a table that by then
 * holds rows on every environment. Declaring the producers the ledger is FOR costs nothing now
 * and is the direction that fails closed — an unlisted producer is a 23514 at the door rather
 * than an origin nobody can filter on.
 */
export const PM_AGENT_RUN_ORIGIN_VALUES = [
    'dispatcher',
    'cli_interactive',
    'cli_subagent',
    'cron',
    'hook',
];
/**
 * The `kind` DICTIONARY of `pm_agent_run.brief_ref` — DM W2 A2 (gh#2670).
 *
 * ── WHY THE OPEN BAG GAINS A CLOSED DISCRIMINATOR AND NOT A SHAPE ──────────
 * `brief_ref` stays `Record<string, unknown>` by type (this file's header says why, and
 * L3 did not change it): two unrelated producers write it — the dispatcher's
 * `{model, effort}` policy brief and the CLI hook's session envelope — and the column
 * carries whatever either of them recorded. What A2's inspector needs is not a shape for
 * the column but a way to tell the two documents APART without guessing from which
 * members happen to be present, which is exactly the job of a discriminator.
 *
 * It lives HERE rather than beside either writer for the reason the vocabularies above
 * do: the API stamps the word and the web panel branches on it, so both halves are
 * meaningless without the other ([[feedback_cross_package_wire_discriminator_single_source]]).
 * `dmAgentRunWrite.service.ts`'s `CLI_HOOK_BRIEF_REF_KIND` is `satisfies`-checked against
 * this tuple, so a renamed member is a `tsc` error at the writer rather than a stamp the
 * reader silently stops recognising.
 *
 * `dispatcher` is a member even though the dispatcher's own writer predates the word and
 * stamps it only as of A2: a projection that published one arm's discriminator and left
 * the other's implicit would make "which document is this" answerable for one producer
 * and not the other.
 */
export const PM_AGENT_RUN_BRIEF_KINDS = ['dispatcher', 'cli_hook'];
/**
 * WHERE A `pm_agent_usage` ROW CAME FROM — §6's "usage KAYNAKTAN yakalanır" made
 * queryable. MOVED here from `schema-pg/pm/pmAgentUsage.ts` at DM W2 A2 (gh#2670).
 *
 * `provider_stream` is the usage block of a provider API response, read off the stream as
 * the request completes. `session_report` is the executor's own transcript/OTEL report,
 * pushed later by `dm.session.report` (§5.1, G5). The two are NOT interchangeable and the
 * column exists so a reader never has to guess which it is holding: they can disagree (a
 * stream that died mid-response has no final usage block; a session report can arrive for
 * a run whose stream was never observed), and a cost total that silently mixes them is a
 * number nobody can defend.
 *
 * ── WHY IT LEFT THE SCHEMA TREE ────────────────────────────────────────────
 * The per-run usage door (A2) publishes `source` as a GROUPING AXIS and the run
 * inspector renders one badge per member, so the web package is now the tuple's second
 * consumer — which is the exact condition this file's header sets for a vocabulary to
 * move. The alternative is the one PR#1965 paid for: the panel either retypes the set (a
 * second authority that starts refusing a value the column can already store) or falls
 * back to bare `string` and loses the compiler.
 *
 * THE COLUMN STILL OWNS ENFORCEMENT. `pmAgentUsage.ts` imports this tuple and feeds it to
 * its `varchar(..., {enum})` config, which is what the baseline generator turns into
 * `chk_pm_agent_usage_source`, and `pmAgentUsage.pg.test.ts` compares the catalog's CHECK
 * back against the symbol — so there is still exactly ONE recording of the vocabulary and
 * the constraint is generated FROM it.
 */
// `otel` (W4-H2 S4c): Claude Code's OTel `claude_code.api_request` log record, one row per
// request via the loopback ingest door; dedup key `raw.requestId`. Never summed with the other two.
export const PM_AGENT_USAGE_SOURCE_VALUES = ['provider_stream', 'session_report', 'otel'];
/**
 * The two words `PM_AGENT_RUN_STATUS_VALUES` ends a run with.
 *
 * Hand-typed and `satisfies`-checked rather than derived, so a SIXTH status does not silently
 * become terminal — and so the liveness derivation, the heartbeat's no-op arm and the
 * reopen gate read ONE tuple instead of three copies of the same two literals.
 */
export const PM_AGENT_RUN_TERMINAL_STATUSES = ['done', 'failed'];
/**
 * The statuses that are alive BY DEFINITION rather than by evidence — DM W2 L3.
 *
 * `queued` has not started (no `started_at`, no producer, nothing to hear from) and
 * `waiting_input` is BLOCKED ON A HUMAN and may sit there for days. Both are states the
 * platform itself put the run in, so "we have not heard from it" says nothing about either:
 * measuring them against a heartbeat window would report every question in the decision
 * inbox as a dead session.
 */
export const PM_AGENT_RUN_UNMEASURED_STATUSES = ['queued', 'waiting_input'];
/**
 * A run's LIVENESS — DM W2 L3, DERIVED at read time and stored nowhere.
 *
 * `ended` is a member rather than a `null`, because a two-value set forces a terminal run
 * into one of two wrong answers and a nullable field is a fourth state every consumer has
 * to handle anyway. A `Record<DmRunLiveness, …>` over this tuple is total.
 *
 * Nothing writes it: a session killed by SIGKILL never sends `SessionEnd`, so it reads
 * `stale` instead of `running` forever, and no reaper invents a terminal state it did not
 * observe.
 *
 * ── THE WINDOW IS ONLY ASKED WHERE A HEARTBEAT PRODUCER EXISTS ─────────────
 * `last_heartbeat_at` has exactly ONE writer (`dm.session.heartbeat`, the CLI `Stop` hook),
 * so three classes of run would otherwise be measured against a signal nothing produces for
 * them — and every one of them would read `stale` while perfectly healthy:
 *
 *   1. TERMINAL (`done`/`failed`) -> `ended`. The lifecycle is over; freshness is not a
 *      question about it.
 *   2. `queued` / `waiting_input` ({@link PM_AGENT_RUN_UNMEASURED_STATUSES}) -> `live`. The
 *      platform put the run there and knows it is there; a `waiting_input` run is blocked on
 *      a person and may sit for days.
 *   3. `host='server'` (the dispatcher's own child) -> `live`. It sends no heartbeat BY
 *      DESIGN — a twenty-minute headless run is normal — so a window verdict would be a
 *      measurement of a signal nothing produces.
 *
 *      THE RESIDUAL, NAMED: a `running` dispatched run has no liveness owner at all.
 *      `dmAgentRunReaper` sweeps `status='queued'` orphans and nothing else, so `live` here
 *      means "no evidence either way", NOT "something else is watching". A W2b question.
 *
 * What is LEFT is exactly the class the window is about: a non-terminal, EXTERNALLY hosted
 * run whose producer is the hook that stamps the column. A false `stale` there costs an
 * operator a glance; a false `live` hides a dead session forever, and that asymmetry is why
 * the window points the way it does.
 */
/**
 * The `?parentRunId=` value that asks for SESSION ROOTS — `parent_run_id IS NULL` — rather
 * than for one run's children (DM W2 A1).
 *
 * ── WHY IT EXTENDS THE EXISTING PARAMETER INSTEAD OF ADDING `?rootsOnly=1` ──
 * Two reasons, and the second is the decisive one.
 *
 * The first is the door's own idiom: `?live=1` was REFUSED in favour of
 * {@link DM_RUN_LIVENESS_VALUES} because a query-string boolean is the shape where
 * `?live=false` coerces to `true`. `?rootsOnly=1` is that same shape, one parameter along.
 *
 * The second is MUTUAL EXCLUSION. `?parentRunId=5&rootsOnly=1` is two narrowings that
 * contradict each other, so a door accepting both needs a documented rule about which
 * wins — and a precedence rule is exactly the kind of thing one caller learns from a bug
 * report ([[feedback_intent_flags_replacing_row_state_need_mutual_exclusion]]). One
 * parameter whose value domain is "a parent id, or the word for having no parent" cannot
 * contradict itself: the two asks are the two arms of one question.
 *
 * A WORD rather than an empty value, because `?parentRunId=` (empty) already coerces to
 * `0` and is refused by the positive-int rule — and "the absent value means the opposite
 * of the present one" is not a contract a caller can read off the parameter.
 */
export const DM_RUN_PARENT_ROOT = 'root';
export const DM_RUN_LIVENESS_VALUES = ['live', 'stale', 'ended'];
/**
 * How long a non-terminal run may go unheard-from before it reads `stale`, in SECONDS.
 *
 * Ten minutes, and the bound it is taken from is the heartbeat's own cadence: the `Stop`
 * hook fires once per assistant TURN, and a turn is unbounded (a turn spending its whole
 * length inside one tool call has no opportunity to heartbeat).
 *
 * SO A LONG TURN READS `stale` WHILE ALIVE, and that direction is the decision: a false
 * `stale` costs an operator a glance, while a false `live` hides a dead session forever —
 * nothing else ever closes one.
 */
export const DM_RUN_STALE_AFTER_SEC = 600;
/**
 * How long a run's heartbeat may go unrefreshed before it stops reading FRESH, in SECONDS
 * — DM W2 A3, and a PRESENTATION constant rather than a second liveness rule.
 *
 * ── IT IS NOT A THIRD LIVENESS VERDICT, AND CANNOT BECOME ONE ──────────────
 * {@link DM_RUN_LIVENESS_VALUES} is derived by the SERVER (`dmRunLivenessExpr`) and is the
 * only authority on whether a run is still alive. This number decides nothing about that:
 * a run whose heartbeat is 121 seconds old is `live` before this constant exists and
 * `live` after it, and the door's answer at this boundary does not change. What it decides
 * is the WORD and the colour a monitor puts beside the server's verdict — "we last heard
 * from this two minutes ago" — which is a statement about the SIGNAL, not about the run.
 *
 * Two minutes, and the bound it is taken from is the producer's cadence: the `Stop` hook
 * fires once per assistant TURN, and an ordinary turn is well under it. So a run that has
 * gone quiet for longer has usually spent the interval inside ONE tool call — worth
 * showing, not worth alarming about, which is why the word for it is `quiet` and the
 * alarming boundary stays {@link DM_RUN_STALE_AFTER_SEC}.
 *
 * ── THE UPPER BOUND IS THE STALE WINDOW **ITSELF**, NOT A COPY OF IT ───────
 * A freshness scale needs two edges and only the lower one is new here. The upper edge is
 * {@link DM_RUN_STALE_AFTER_SEC}, read directly by the consumer, because a second `600`
 * declared beside this line is exactly the drift that makes a badge disagree with the
 * `?liveness=` filter it sits next to ([[feedback_cross_package_wire_discriminator_single_source]]).
 *
 * `FRESH < STALE_AFTER` is what makes the scale ORDERED at all — an inversion would make
 * the middle band empty and every quiet run read as fresh — and it is pinned in
 * `__tests__/pmAgentRunWire.test.ts` rather than left to a reader comparing two literals in
 * different paragraphs.
 *
 * SECONDS, like its sibling: the two are compared against each other and against one
 * elapsed value, so a unit change on one side is a silent factor of sixty.
 */
export const DM_RUN_HEARTBEAT_FRESH_SEC = 120;
/**
 * The `kind` DICTIONARY of `pm_agent_run.result_ref` — the discriminator's closed
 * value set, HAND-AUTHORED beside the union rather than derived from it.
 *
 * ── WHY A SECOND DECLARATION IS THE POINT AND NOT THE DEFECT ────────────────
 * Deriving this from {@link agentRunResultRefSchema} would make every lockstep
 * assertion over the pair TAUTOLOGICAL — `KINDS.length` computed from the union
 * can never disagree with the union ([[feedback_derived_count_axis_not_independent]]).
 * The two are therefore INDEPENDENT AXES bound by machinery: a compile-time parity
 * pin below (which collapses to `never`, i.e. a `tsc` error, if either side gains
 * or loses a member) and a runtime lockstep in `__tests__/pmAgentRunWire.test.ts`
 * that reads the kinds back OUT of the union and compares them here. This is the
 * shape `PM_EXTERNAL_KINDS` + `pmEdgeExternalTargetSchema` already established in
 * `pm.schemas.ts`.
 *
 * It is also the enumeration a per-kind consumer needs: a renderer that has to show
 * a run's outcome cannot read a zod internal, and a `Record<AgentRunResultRefKind,
 * …>` over this tuple is what turns "a kind was added and nobody decided how it
 * renders" into a compile error at that consumer.
 */
export const AGENT_RUN_RESULT_REF_KINDS = ['report', 'plan_item', 'error'];
/**
 * Text ceilings, named once and shared by the members below.
 *
 * `PROSE`, not `SUMMARY`: it bounds `report.summary` AND `error.message`, which are
 * two different fields with one ceiling. Naming it after the first consumer would
 * make the second one look like it had borrowed somebody else's limit — and would
 * quietly discourage the next editor from touching a number they believed belonged
 * to a field they were not changing.
 *
 * NOT new numbers: `2000` is the authored acceptance-criterion text's bound
 * (`pmAcceptanceCriterionSchema.text`; it was the jsonb
 * `pm_item.acceptance_criteria[].text` field's bound until migration 0049 dropped
 * that column, and the authoring schema kept it) and `4000` is
 * `pm_question.answer`'s — the pm family's existing authored-text
 * ceilings. Reusing them keeps the family with two text bounds rather than four,
 * and a bound is what makes `result_ref` a pointer bag instead of a place an
 * unbounded agent transcript can land.
 *
 * ── EXPORTED AS OF Faz 4 dilim-2 PR-4, AND THE EXPORT IS THE POINT ─────────
 * They were module-private while nothing produced a `result_ref`. The dispatcher
 * does, and it has to BOUND the text it collected from a `claude -p` child before it
 * offers the document to {@link agentRunResultRefSchema} — an agent's stdout is
 * unbounded, so "truncate to the ceiling" is a step the writer performs, not one the
 * parse can perform for it (the parse's only options are accept and refuse).
 *
 * A writer that retyped `2000` would be a SECOND authority on the same number, and
 * the one that drifts in the direction nothing notices: raise the ceiling here and
 * the dispatcher keeps cutting at the old one, with every suite green and no field
 * ever refused. So the bound has one home and its consumer imports it, which is the
 * rule this file's own header states for the vocabularies.
 */
export const RESULT_REF_PROSE_MAX = 2000;
export const RESULT_REF_TAIL_MAX = 4000;
/**
 * `pm_agent_run.result_ref` — the run's OUTCOME, as a CLOSED discriminated union.
 *
 * ── WHY THIS COLUMN GETS A SHAPE NOW ────────────────────────────────────────
 * PR-8 shipped it as an open `Record<string, unknown>` and said exactly why: the
 * surface that CONSTRUCTS it is the Faz 4 dispatcher, and "a shape guessed here
 * would be indistinguishable from a decided one for every later reader". Faz 4 is
 * here and the dispatcher's outcomes are enumerable from spec §6.3, so this is the
 * decided one.
 *
 * ── THE THREE MEMBERS ARE THE THREE WAYS §6.3 SAYS A RUN CAN END ────────────
 *   `report`    — the ordinary terminal outcome: "stdout/rapor `result_ref`'e".
 *   `plan_item` — "çıktı `dm.plan` item'ı olarak MCP'den geri yazılır": the run
 *                 PRODUCED a row, and the outcome is that row's address. Storing
 *                 the id rather than a rendered copy means the plan has one home
 *                 and the run points at it.
 *   `error`     — "fail → status=failed + event": the class and the message, so a
 *                 reader can tell a refused capability from a crashed process
 *                 without opening a log store.
 *
 * ── IT IS A POINTER BAG, AND `stdoutTail` IS THE HONEST VERSION OF THAT ─────
 * PR-8's header set the rule: "agent stdout is unbounded and belongs in a log
 * store, so what lands here is the summary plus whatever locates the full output".
 * There IS no log store today, so this union declares NO locator: inventing a
 * `logRef` URI scheme with nothing to resolve it would be the guessed shape PR-8
 * refused, one field to the left. What ships instead is a BOUNDED TAIL, named for
 * what it is so no reader mistakes it for the full transcript, and a locator is a
 * pure ADDITION (an optional field on `report`) the day a store exists.
 *
 * ── EVERY MEMBER IS `.strict()` AND AN UNKNOWN `kind` THROWS ────────────────
 * Fail-closed, for `pmEdgeExternalTargetSchema`'s reason: a value stored as an
 * opaque bag because the parser shrugged is how a jsonb column stops having a
 * contract. `.strict()` also makes a MISSPELLED optional field (`stdout_tail`) a
 * refusal instead of a silently dropped one.
 *
 * ── WHAT IT DOES *NOT* DO: PUBLISH ITSELF ──────────────────────────────────
 * `DmAgentRunWire` still withholds `resultRef`, deliberately and unchanged — it is
 * the pointer to an agent's raw output, the least-vetted content the platform will
 * hold, and giving it a TYPE is not the same decision as giving it a READER. That
 * decision belongs to the slice that ships the writer.
 */
export const agentRunResultRefSchema = z.discriminatedUnion('kind', [
    z
        .object({
        kind: z.literal('report'),
        /**
         * The one-paragraph outcome. `min(1)`: an empty summary is not a summary,
         * and the honest encoding of "no outcome recorded" is SQL NULL on the
         * column — which the column keeps (it is NULLABLE).
         */
        summary: z.string().min(1).max(RESULT_REF_PROSE_MAX),
        /** The LAST slice of the process output — bounded, never the whole of it. */
        stdoutTail: z.string().min(1).max(RESULT_REF_TAIL_MAX).optional(),
    })
        .strict(),
    z
        .object({
        kind: z.literal('plan_item'),
        /** The `pm_item.id` the run wrote. Positive: an id is bounded before it is stored. */
        itemId: z.number().int().positive(),
    })
        .strict(),
    z
        .object({
        kind: z.literal('error'),
        /**
         * The ERROR CLASS, not a rendered sentence — `PmGateDeniedError`,
         * `DispatchTimeoutError`. Bounded at 128 like `pm_question.asked_by`, the
         * pm family's identifier ceiling, because that is what this is.
         */
        errorClass: z.string().min(1).max(128),
        message: z.string().min(1).max(RESULT_REF_PROSE_MAX),
        /**
         * The LAST slice of the process output — bounded, never the whole of it.
         * The SAME field and the same ceiling as `report`'s, deliberately: it is the
         * same fact about the same child, and a failed run is the case where it is
         * worth the most. `message` says the run was killed at the ceiling or exited
         * non-zero; it cannot say WHAT the child was doing when that happened, and
         * with no log store to point at (see this union's header) an operator who
         * wants that today has nothing to open.
         *
         * OPTIONAL, because a run can fail with no output at all — a spawn error is
         * a child that never ran. Absent is the honest encoding of that; `min(1)`
         * makes `''` a refusal rather than a tail claiming to hold nothing.
         *
         * ── IT LANDS IN THE COLUMN, NOT ON THE WIRE ────────────────────────────
         * `DmAgentRunWire` still withholds `resultRef` entirely, unchanged by this
         * field — the reasoning in this union's header ("giving it a TYPE is not the
         * same decision as giving it a READER") applies to a failure tail with more
         * force than to a report's, not less. This is stored diagnostics for whoever
         * reads the row directly; publishing it is a separate decision for a slice
         * that ships a reader.
         */
        stdoutTail: z.string().min(1).max(RESULT_REF_TAIL_MAX).optional(),
    })
        .strict(),
]);
const _assertResultRefKindParity = true;
void _assertResultRefKindParity;
/**
 * The CLOSED member dictionary of `pm_agent_run.env_fingerprint` — K-G4-6, SRS G4 PR-4
 * (gh#2471). The vocabulary, hand-authored beside the schema below rather than derived
 * from it, for {@link AGENT_RUN_RESULT_REF_KINDS}'s reason: a member set computed from
 * the object it is supposed to police can never disagree with it, and every lockstep
 * assertion over the pair would be a tautology
 * ([[feedback_derived_count_axis_not_independent]]). The two axes are bound by machinery
 * instead — a compile-time parity pin below, and the runtime triple pin in
 * `dmToolParity.test.ts` that also drives the DECLARATION a model reads.
 *
 * It is the enumeration the declaration needs: `ToolParameter` has no field for a
 * nested shape, so `dm.session.report`'s `envFingerprint` parameter announces its member
 * menu by JOINING this tuple into its description. A member added without a word to the
 * model would otherwise be a member only the parser knows about.
 */
export const PM_ENV_FINGERPRINT_MEMBERS = ['revisionIds', 'skillCatalog'];
/**
 * How many environment-artifact revisions ONE fingerprint may cite.
 *
 * NOT A NEW NUMBER: it is `PM_ENV_MANIFEST_CAP`, the ceiling the environment manifest
 * already serves under, and the two are bound by a runtime pin in
 * `pmEnvRunFingerprint.pg.test.ts` rather than by this sentence. The binding is a real
 * lockstep and not tidiness — a project serves at most one APPROVED revision per
 * artifact, so a session that read the WHOLE of its project's environment cites exactly
 * as many revisions as the manifest lists. A cap below the manifest's would make a
 * complete environment unreportable; a cap above it would admit a citation set no
 * manifest could have produced.
 *
 * The constant cannot simply IMPORT the manifest cap: that bound lives on the API's read
 * service (`modules/pm/pmEnvRead.service.ts`) and this package is upstream of it. So the
 * value is restated here and the DIRECTION of the dependency is asserted by the test —
 * the same posture `PM_ENV_READ_SERVED_KINDS` takes toward its own derivation.
 */
export const PM_ENV_FINGERPRINT_MAX_REVISIONS = 200;
/**
 * The grammar of {@link PmEnvFingerprintSkillCatalog.digest} — 64 lowercase hex
 * characters, i.e. a sha256 written the way `pm_env_artifact_revision.content_hash` is
 * written (`pmEnvContentHash`). ANCHORED at both ends, so a digest with anything around
 * it is a refusal rather than a match somewhere inside a longer string.
 *
 * A GRAMMAR AND NOT A DERIVATION — AND THE DERIVATION NOW EXISTS, ONE SCREEN DOWN.
 * PR-4 shipped this pattern alone and wrote the deferral verbatim: what the digest is
 * computed over is the executor's business and `dm init` (PR-5) publishes the canonical
 * recipe. PR-5 pays it — {@link PM_ENV_SKILL_CATALOG_DIGEST_RECIPE} and
 * {@link buildPmEnvSkillCatalogPreimage} below — and the two stay SEPARATE rather than
 * folding into one export, because they answer different questions and one of them is
 * still open: this pattern says what a WELL-FORMED value looks like and is what the parse
 * seam enforces on any writer; the recipe says how THIS platform's executor derives one.
 * A digest arriving from an executor that derived it some other way is still well-formed,
 * and the seam still has no way to tell — which is why the reader publishes the member as
 * a self-report rather than as a resolved fact (`pmEnvRunFingerprint.service.ts`).
 */
export const PM_ENV_SKILL_CATALOG_DIGEST_PATTERN = /^[0-9a-f]{64}$/;
/**
 * The CANONICAL RECIPE's identity — the first line of every preimage
 * {@link buildPmEnvSkillCatalogPreimage} builds (spec §5.5, PR-5 note 26).
 *
 * ── IT IS IN THE PREIMAGE, NOT BESIDE IT, AND THAT IS DOMAIN SEPARATION ────
 * The fold below hashes a list of integers. Integers are the most reusable shape there
 * is, so without a domain tag ANY other feature that ever hashes a sorted id list the
 * same way produces byte-identical digests for an unrelated set — and `skillCatalog.digest`
 * is compared for EQUALITY across runs, which is precisely where an accidental collision
 * reads as "these two sessions had the same catalogue". Prefixing the preimage makes the
 * hash a statement about a skill catalogue rather than about some numbers.
 *
 * ── AND IT CARRIES A VERSION, WHICH IS WHAT MAKES A RECIPE CHANGE HONEST ───
 * `/1` is not decoration. Any edit to the fold — a different order, a different separator,
 * a field added — changes every digest it produces, so runs recorded before and after
 * would silently stop comparing. Bumping the suffix in the SAME edit makes the break
 * self-describing: the recipe id travels on `dm init`'s report beside the digest, so an
 * operator comparing two runs can see that the RECIPE differs rather than concluding the
 * catalogue did ([[feedback_intent_key_single_canonical_derivation]]).
 *
 * NOT STORED IN `env_fingerprint`. The member's shape is CLOSED (K-G4-6) and adding a
 * third field to it would be a dictionary widening, which this constant does not earn:
 * the recipe id is a property of the DERIVATION, and the fingerprint records the RESULT.
 */
export const PM_ENV_SKILL_CATALOG_DIGEST_RECIPE = 'pm-env-skill-catalog/1';
/**
 * The canonical PREIMAGE of {@link PmEnvFingerprintSkillCatalog.digest} — PR-5's payment
 * of PR-4 note 19's deferral (spec §5.5, K-G4-4).
 *
 * ── WHAT THE CATALOGUE *IS*, AND THEREFORE WHAT THE FOLD MAY CONTAIN ───────
 * K-G4-4: the skill catalogue is the `kind='skill'` PROJECTION of the environment
 * manifest — never a table. So its "version" is a value DERIVED from the set of APPROVED
 * skill revisions a project serves, and the derivation folds REVISION IDS AND NOTHING
 * ELSE. That is the same rule note 19 states for the citation member, applied to a fold:
 * a revision id determines its artifact, and the artifact determines slug, name, kind and
 * project — so restating any of them here would put a second authority inside a value
 * whose only job is to be equal or unequal.
 *
 * The bytes are covered WITHOUT being hashed, and this is the property that makes an
 * ids-only fold sufficient rather than merely tidy: an APPROVED revision is FROZEN at the
 * database (`pm_env_artifact_revision_frozen`), so the map from id to text cannot change
 * under a recorded digest. A catalogue whose content changed did so by APPROVING a new
 * revision, which is a new id, which is a different digest. Folding `content_hash` too
 * would add a column that can only ever agree — and a field that cannot disagree is a
 * field whose disagreement nobody would ever handle ([[feedback_second_door_table_scope]]).
 *
 * ── THE GRAMMAR, EXACTLY, BECAUSE A DIGEST IS A PIN ────────────────────────
 *   line 1: {@link PM_ENV_SKILL_CATALOG_DIGEST_RECIPE}
 *   line n: one revision id, DECIMAL, ASCENDING, each line `\n`-terminated
 *
 * Sorted because a catalogue is a SET and the manifest's own order is `(kind, slug)` —
 * a fold that took arrival order would give one project two digests depending on how the
 * rows came back, which is the churn `getPmProjectTechProfile`'s `ORDER BY slug` note
 * argues against, arriving through a hash. Line-terminated rather than joined, so the
 * EMPTY catalogue is `RECIPE\n` and not an ambiguous `RECIPE\n\n`; a project serving no
 * skills has a digest, and it is a value rather than an absence — the same discipline the
 * empty `revisionIds` array takes ([[feedback_failopen_close_sentinel_default_callers]]).
 *
 * ── IT REFUSES RATHER THAN NORMALISES, AND THE REFUSAL IS UNREACHABLE ──────
 * A duplicate, a non-integer or a non-positive id THROWS. None of the three can arrive
 * from the manifest — revision ids are primary keys and a project serves at most one
 * approved revision per artifact — so this arm is the fail-loud one that holds if a future
 * caller projects the catalogue some other way. Silently de-duplicating would be worse
 * than either: it would answer a digest for a catalogue the caller did not describe
 * (the same reason `revisionIds` refuses a repeat at the parse seam).
 *
 * PURE, AND DELIBERATELY NOT THE HASH. This package is imported by the browser bundle, so
 * it declares the preimage and the API hashes it with the registry's OWN
 * `pmEnvContentHash` — one spelling of "the sha256 of this text" in the tree, which is the
 * same argument that keeps `dm env verify` from carrying its own `createHash`.
 */
export function buildPmEnvSkillCatalogPreimage(revisionIds) {
    const seen = new Set();
    for (const id of revisionIds) {
        if (!Number.isInteger(id) || id <= 0) {
            throw new Error(`pm env skill catalogue: ${String(id)} is not a positive integer revision id. The ` +
                'digest is a pin over a set of approved revisions, so a value that cannot be one ' +
                'is refused rather than folded.');
        }
        if (seen.has(id)) {
            throw new Error(`pm env skill catalogue: revision ${String(id)} appears twice. A catalogue is a SET; ` +
                'a repeated id means the projection is wrong, and de-duplicating it here would ' +
                'answer a digest for a catalogue nobody described.');
        }
        seen.add(id);
    }
    const ascending = [...seen].sort((a, b) => a - b);
    return `${PM_ENV_SKILL_CATALOG_DIGEST_RECIPE}\n${ascending.map((id) => `${String(id)}\n`).join('')}`;
}
/**
 * `pm_agent_run.env_fingerprint` — the session's ENVIRONMENT, as a CLOSED document
 * (spec §5.5 / §6, K-G4-4 · K-G4-5 · K-G4-6).
 *
 * ── WHY THIS COLUMN GETS A SHAPE NOW ───────────────────────────────────────
 * G5 PR-5 shipped it open and wrote the closing condition down verbatim: "it closes when
 * a reader exists that must branch on a member". SRS G4 built the Environment Registry
 * the members refer TO (PR-1 the two-table revision model, PR-2 the manifest, PR-3 the
 * drift protocol), and this PR ships the reader — `GET /api/dm/agent-runs/:id/environment`
 * resolves every cited revision against `pm_env_artifact_revision` and cannot do it
 * without branching on `revisionIds`. So the condition is MET rather than waived, and
 * this is the decided shape rather than a guessed one — `AgentRunResultRef`'s arc, one
 * column to the right.
 *
 * ── THE TWO MEMBERS, AND WHY NEITHER IS THE OTHER ──────────────────────────
 *   `revisionIds` — the registry documents that were IN FORCE for the session, cited at
 *     REVISION level and never at artifact level. K-G4-5 is the whole reason the registry
 *     is two tables: "fingerprint bir ŞEYE değil bir SÜRÜME atıf yapmalıdır" — an artifact
 *     id names a row that gets edited, so a fingerprint recorded last week would name
 *     today's text and the question it was recorded to answer would be unanswerable at
 *     exactly the moment it was asked.
 *   `skillCatalog` — the VERSION of the skill catalogue the session had AVAILABLE (K-G4-4:
 *     the catalogue is the `kind='skill'` projection of the manifest, never a table of its
 *     own). It is not derivable from the member above and that is why it exists: the
 *     citations name what was IN FORCE, the catalogue names what was INVOCABLE, and a
 *     session that could have reached ten skills and read two has an environment neither
 *     number alone describes.
 *
 * NOTHING ELSE, and unknown members are REFUSED rather than carried (K-G4-6). The
 * dictionary widens only by a spec note, which is what keeps a jsonb column from becoming
 * the place any writer can add a field no reader agreed to.
 *
 * ── WHAT IS DELIBERATELY *NOT* A MEMBER: `kind`, `slug`, `projectId` ───────
 * A citation carries an id and nothing else. The revision determines its artifact, and the
 * artifact determines its kind, slug, name and project — so restating any of them here
 * would create a pair that can DISAGREE, and a reader would have to choose which side to
 * believe. `dmEnvApproveToolSchema` refuses the same three fields for the same reason, and
 * the reader resolves all of them from the registry instead.
 *
 * ── AN EMPTY `revisionIds` IS A STATEMENT, NOT A BLANK ─────────────────────
 * The member is REQUIRED and the empty array is ACCEPTED, which is the same discipline the
 * usage lines take toward a dimension nobody measured: an explicit `0` is a decision with
 * an author, an omitted field is the platform choosing on the caller's behalf
 * ([[feedback_failopen_close_sentinel_default_callers]]). `{"revisionIds": []}` says "this
 * session ran on NOTHING from the registry", which under §5.5's "sessiz sapma
 * desteklenmez" is one of the most interesting things a session can report — refusing it
 * would delete the signal rather than record it.
 *
 * `{}` is still refused, at the parse seam, and the two are not the same document: an
 * empty OBJECT names no member at all (silence), an empty ARRAY is an author's zero.
 *
 * ── NO SIZE BOUND HERE, AND THE BYTE CAP IS NOW UNREACHABLE ────────────────
 * The member caps bound the document structurally: at most
 * {@link PM_ENV_FINGERPRINT_MAX_REVISIONS} integers plus a 64-character digest and a
 * count, which cannot approach `DM_SESSION_REPORT_FINGERPRINT_MAX_BYTES`. That cap STAYS
 * at the parse seam anyway — it is the arm that fails closed if these caps are ever
 * widened, the same reason `dmSessionReport.service.ts` keeps its own unreachable
 * `candidates.length > 1` check — and its unreachability is ASSERTED rather than assumed
 * (`dmSessionReport.pg.test.ts`).
 */
export const pmAgentRunEnvFingerprintSchema = z
    .object({
    /**
     * `pm_env_artifact_revision.id` values. POSITIVE integers, DUPLICATE-FREE, and capped
     * at the manifest's own ceiling.
     *
     * The duplicate refusal is not tidiness: a citation set is a SET, and a document that
     * names one revision twice is one whose author cannot have meant either reading —
     * "twice as much of one document" is not a state an environment can be in. Refused at
     * the seam rather than de-duplicated silently, because a silent fold would store a
     * document the caller did not send.
     *
     * NOT VALIDATED AGAINST THE REGISTRY, and that is a decision rather than an omission.
     * A write-time existence check would answer "accepted" for an id that resolves and
     * refuse one that does not, which hands a `pm.agent_run.telemetry` holder — a key that
     * carries NO read over `pm_env_*` — an enumeration oracle over the registry
     * ([[feedback_ambiguity_refusal_enumeration_oracle]]). Resolution is therefore the
     * READER's job, tenant-scoped, and an id that names nothing servable is reported as
     * `unresolved` rather than refused at the door.
     */
    revisionIds: z
        .array(z.number().int().positive())
        .max(PM_ENV_FINGERPRINT_MAX_REVISIONS)
        .superRefine((ids, ctx) => {
        if (new Set(ids).size === ids.length)
            return;
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'revisionIds must not repeat a revision — a fingerprint cites a SET of ' +
                'documents, and a duplicate is refused rather than folded away',
        });
    }),
    /**
     * The skill catalogue's version, as the EXECUTOR derived it.
     *
     * Both fields are required together, because either alone is a partial claim: a digest
     * with no count cannot be read as "how big was the catalogue", and a count with no
     * digest cannot be compared against another session's.
     */
    skillCatalog: z
        .object({
        /** sha256 of the catalogue projection — {@link PM_ENV_SKILL_CATALOG_DIGEST_PATTERN}. */
        digest: z.string().regex(PM_ENV_SKILL_CATALOG_DIGEST_PATTERN),
        /**
         * How many skills the catalogue held. `0` is legal — a project may serve none —
         * and the ceiling is the manifest's, because the catalogue is a PROJECTION of the
         * manifest (K-G4-4) and cannot hold more entries than the manifest can list.
         */
        count: z.number().int().min(0).max(PM_ENV_FINGERPRINT_MAX_REVISIONS),
    })
        .strict()
        .optional(),
})
    // `.strict()` IS THE CLOSED DICTIONARY (K-G4-6). An unknown member is a REFUSAL and
    // never a dropped field: a document stored as an opaque bag because the parser shrugged
    // is how a jsonb column stops having a contract, and this column spent a whole phase
    // being exactly that on purpose.
    .strict();
const _assertEnvFingerprintMemberParity = true;
void _assertEnvFingerprintMemberParity;
//# sourceMappingURL=pmAgentRunWire.js.map